import React, { useContext, useEffect, useRef, useState } from 'react'
import { VscMenu } from "react-icons/vsc";
import { Link, NavLink, redirect } from 'react-router-dom';
import { clearLoginInfo } from '../../configurations/auth/clearLoginInfo';
import { authContext } from '../../configurations/auth/authContext';
import { getLoginInfo } from '../../configurations/auth/getLoginInfo';
import { useTopMenuOpen } from './topMenuContext';
import Dp from '../app/users/Dp';
import { FaChartPie, FaHospital, FaPen, FaPlus, FaStethoscope } from 'react-icons/fa'
import { FiChevronDown, FiKey, FiLogOut, FiX } from "react-icons/fi";
import { HiChevronDown, HiOutlineUserCircle } from "react-icons/hi2";
import { FaRupeeSign, FaBookMedical, FaHistory } from "react-icons/fa";
import GlobalLoader from './GlobalLoader';
import { FaBriefcaseMedical } from "react-icons/fa6";
import useDeregisterPush from '../../configurations/push/useDeregisterPush';

export default function SideBar({ isDoctor = true }) {
    const [isLogin, setIsLogin] = useContext(authContext);
    const [isOpen, setIsOpen] = useState(false)
    const [isTopMenuOpen, changeTopMenu, closeTopMenu] = useTopMenuOpen()
    const userData = getLoginInfo();
    const [isDropdownOpen, setIsDropdownOpen] = useState({ user: false, opdcenter: false })
    const [menuOpen, setMenuOpen] = useState(false);
    const menuref = useRef();
    const { handleDeregister } = useDeregisterPush()
    useEffect(() => {
        const close = (e) => {
            if (menuref.current && !menuref.current.contains(e.target)) setMenuOpen(false)
        };
        document.addEventListener("click", close);
        return () => document.removeEventListener("click", close);
    }, []);

    function toggleMenu() { setMenuOpen(!menuOpen); }
    function closeMenu() { setMenuOpen(false); setIsOpen(false) }

    const isDropdownChange = (name) => {
        switch (name) {
            case "user": { setIsDropdownOpen({ user: !isDropdownOpen.user, opdcenter: false }); break; }
            case "opdcenter": { setIsDropdownOpen({ opdcenter: !isDropdownOpen.opdcenter, user: false }); break; }
        }
    }

    const changeIsOpen = () => setIsOpen(!isOpen)

    const logout = async () => {
        await handleDeregister()
        clearLoginInfo()
        setIsLogin(false)
        redirect("/login")
    }

    // shared classes for nav links
    const linkBase = "flex items-center gap-3 px-3 py-2.5 rounded-xl text-[15px] text-gray-700 transition-colors hover:bg-brand-50 hover:text-brand-700";
    const navItem = ({ isActive }) => isActive
        ? "flex items-center gap-3 px-3 py-2.5 rounded-xl text-[15px] font-semibold text-brand-700 bg-brand-50 ring-1 ring-brand-100"
        : linkBase;

    return (
        <>
            <nav className="fixed top-0 z-50 w-full border-b border-gray-100 bg-white/90 shadow-soft backdrop-blur-xl">
                <div className="flex h-[60px] items-center justify-between gap-2 px-3 sm:px-4">

                    {/* left: menu + logo */}
                    <div className="flex min-w-0 items-center gap-2">
                        <button
                            onClick={() => { changeIsOpen(); closeTopMenu(); }}
                            type="button"
                            aria-label={isOpen ? "Close sidebar" : "Open sidebar"}
                            aria-expanded={isOpen}
                            className="group relative grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600 text-white shadow-brand ring-1 ring-white/20 transition-all duration-300 hover:shadow-lg hover:shadow-brand-500/40 hover:ring-white/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 active:scale-90"
                        >
                            <span className="sr-only">{isOpen ? "Close sidebar" : "Open sidebar"}</span>

                            {/* shine sweep on hover */}
                            <span
                                aria-hidden="true"
                                className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-500 group-hover:translate-x-full"
                            />

                            {/* animated hamburger → X */}
                            <span aria-hidden="true" className="relative flex h-4 w-5 flex-col items-center justify-between">
                                <span
                                    className={`h-0.5 w-full rounded-full bg-white transition-all duration-300 ease-out ${isOpen ? "translate-y-[7px] rotate-45" : "group-hover:w-full"}`}
                                />
                                <span
                                    className={`h-0.5 rounded-full bg-white transition-all duration-300 ease-out ${isOpen ? "w-0 opacity-0" : "w-3/4 group-hover:w-full"}`}
                                />
                                <span
                                    className={`h-0.5 w-full rounded-full bg-white transition-all duration-300 ease-out ${isOpen ? "-translate-y-[7px] -rotate-45" : "group-hover:w-full"}`}
                                />
                            </span>
                        </button>
                        <Link
                            to="/home"
                            className="flex shrink-0 items-center rounded-lg p-1 transition hover:bg-gray-50 active:scale-95"
                            onClick={() => { setIsOpen(false); closeTopMenu(); }}
                        >
                            <img src="/assets/images/logo.svg" className="h-7 w-auto sm:h-8" alt="Doctora" />
                        </Link>
                    </div>

                    {/* right: user menu */}
                    <div className="flex min-w-0 items-center">
                        <div
                            className="relative min-w-0"
                            ref={menuref}
                            onKeyDown={(e) => e.key === "Escape" && closeMenu()}
                        >
                            <button
                                onClick={toggleMenu}
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                                className="flex min-w-0 items-center gap-1.5 rounded-full p-1 transition hover:bg-gray-100 sm:gap-2 sm:pl-2.5"
                            >
                                <span className="hidden max-w-[140px] truncate text-sm font-medium text-gray-700 sm:block md:max-w-[220px]">
                                    Dr. {userData.fullName}
                                </span>
                                <Dp user={userData} height={"h-9"} width={"w-9"} />
                                <FiChevronDown
                                    className={`hidden shrink-0 text-gray-400 transition-transform duration-200 sm:block ${menuOpen ? "rotate-180" : ""}`}
                                />
                            </button>

                            {menuOpen && (
                                <div
                                    role="menu"
                                    className="absolute right-0 z-50 mt-2 w-60 max-w-[calc(100vw-1.5rem)] origin-top-right animate-scale-in overflow-hidden rounded-2xl bg-white text-sm shadow-pop ring-1 ring-gray-100"
                                >
                                    {/* user header */}
                                    <div className="flex items-center gap-3 bg-gradient-to-r from-brand-50 to-white px-4 py-3">
                                        <div className="shrink-0">
                                            <Dp user={userData} height={"h-10"} width={"w-10"} />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="truncate font-semibold text-gray-900">Dr. {userData.fullName}</p>
                                            <p className="truncate text-xs text-gray-500">{userData.userName}</p>
                                        </div>
                                    </div>

                                    {/* links */}
                                    <div className="p-1.5">
                                        <Link
                                            to="/profile"
                                            role="menuitem"
                                            className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-gray-700 transition hover:bg-gray-50 active:bg-gray-100"
                                            onClick={closeMenu}
                                        >
                                            <HiOutlineUserCircle className="text-lg text-brand-500" /> Profile
                                        </Link>
                                        <Link
                                            to="/change-password"
                                            role="menuitem"
                                            className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-gray-700 transition hover:bg-gray-50 active:bg-gray-100"
                                            onClick={closeMenu}
                                        >
                                            <FiKey className="text-lg text-purple-500" /> Change Password
                                        </Link>
                                    </div>

                                    {/* logout — alag section mein taaki galti se na dabe */}
                                    <div className="border-t border-gray-100 p-1.5">
                                        <button
                                            role="menuitem"
                                            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-red-600 transition hover:bg-red-50 active:bg-red-100"
                                            onClick={() => { closeMenu(); logout(); }}
                                        >
                                            <FiLogOut className="text-lg" /> Logout
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <GlobalLoader />
            </nav>

            {/* ---------- Drawer ---------- */}
            <aside className={"fixed top-0 left-0 z-50 w-72 max-w-[82vw] h-screen pt-[10px] pb-4 transition-transform duration-300 ease-out bg-white border-r border-gray-100 "
                + (isOpen ? "translate-x-0 shadow-pop" : "-translate-x-full")}>
                <div className="h-full px-3 py-3 overflow-y-auto flex flex-col">
                    {/* brand header */}
                    <div className="flex items-center justify-between gap-2 px-2 pb-3 mb-2 border-b border-gray-100">
                        <Link to="/home" onClick={changeIsOpen} className="flex items-center">
                            <img src="/assets/images/logo.svg" alt="Doctora" className="h-7 w-auto" />
                        </Link>
                        <button onClick={changeIsOpen} className="grid place-items-center h-8 w-8 rounded-lg text-gray-500 hover:bg-gray-100 lg:hidden"><FiX /></button>
                    </div>
                    <span className="px-2 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">Menu</span>

                    <ul className="space-y-1">
                        <li onClick={changeIsOpen}>
                            <NavLink to="home" className={navItem}><FaChartPie className="text-brand-500 text-lg" /> Dashboard</NavLink>
                        </li>
                    </ul>

                    {/* primary CTA */}
                    <Link to="/startopd" onClick={changeIsOpen}
                        className="mt-3 flex items-center gap-3 px-3 py-3 rounded-2xl text-white bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-soft hover:brightness-105 active:scale-[.99] transition">
                        <span className="grid place-items-center h-9 w-9 rounded-xl bg-white/20"><FaStethoscope className="text-lg" /></span>
                        <span className="flex-1">
                            <span className="block font-semibold leading-5">Start OPD</span>
                            <span className="block text-xs text-emerald-50/90">Begin today's session</span>
                        </span>
                    </Link>

                    <p className="px-2 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">Manage</p>
                    <ul className="space-y-1">
                        <li onClick={changeIsOpen}>
                            <NavLink to="/lookout" className={navItem}><FaBriefcaseMedical className="text-lime-500 text-lg" /> Look Out OPD</NavLink>
                        </li>
                        <li onClick={changeIsOpen}>
                            <NavLink to="/opdcenter/all" className={navItem}><FaHospital className="text-blue-600 text-lg" /> Centers</NavLink>
                        </li>
                        <li onClick={changeIsOpen}>
                            <NavLink to="/opdregister" className={navItem}><FaHistory className="text-rose-500 text-lg" /> OPD History</NavLink>
                        </li>
                        {isDoctor &&
                            <li onClick={changeIsOpen}>
                                <NavLink to="/fastnotes/view" className={navItem}><FaPen className="text-purple-500 text-lg" /> Fast Note</NavLink>
                            </li>
                        }
                    </ul>

                    <p className="px-2 pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">Billing</p>
                    <ul className="space-y-1">
                        <li>
                            <button onClick={() => isDropdownChange('opdcenter')} type="button" className={linkBase + " w-full"}>
                                <FaRupeeSign className="text-teal-600 text-lg" />
                                <span className="flex-1 text-left">Recharge</span>
                                <HiChevronDown className={"w-4 h-4 transition-transform " + (isDropdownOpen.opdcenter ? "rotate-180" : "")} />
                            </button>
                            <ul className={(isDropdownOpen.opdcenter ? "max-h-40" : "max-h-0") + " overflow-hidden transition-all duration-300 pl-3 space-y-1"}>
                                <li onClick={changeIsOpen}>
                                    <Link to="/recharge" className={linkBase + " pl-7"}><FaPlus className='text-emerald-600' /> New Recharge</Link>
                                </li>
                                <li onClick={changeIsOpen}>
                                    <Link to="/rechargehistory" className={linkBase + " pl-7"}><FaBookMedical className='text-amber-600' /> Recharge History</Link>
                                </li>
                            </ul>
                        </li>
                    </ul>

                    <div className="mt-auto pt-4">
                        <button onClick={() => { closeMenu(); logout(); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 hover:bg-red-50 transition">
                            <FiLogOut className="text-lg" /> Logout
                        </button>
                    </div>
                </div>
            </aside>

            {isOpen && <div onClick={() => { changeIsOpen(); closeTopMenu(); }} className="bg-ink-900/50 backdrop-blur-sm fixed inset-0 z-40 animate-fade-in"></div>}
        </>
    )
}
